import { useState } from "react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@/components/ui";
import { notify } from "@/lib/notify";
import { useUpdateProduct } from "../api";
import type { Product } from "@/types";

interface ProductDetailsFormProps {
  product: Product;
  categories: { id: string; name: string }[];
}

export function ProductDetailsForm({
  product,
  categories,
}: ProductDetailsFormProps) {
  const updateProduct = useUpdateProduct();

  const [form, setForm] = useState({
    name: product.name,
    description: product.description ?? "",
    imageUrl: product.imageUrl ?? "",
    categoryId: product.categoryId ?? "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateProduct.mutateAsync({
        id: product.id,
        data: {
          name: form.name,
          description: form.description || undefined,
          sku: product.sku,
          imageUrl: form.imageUrl || undefined,
          categoryId: form.categoryId || undefined,
        },
      });
      notify.success("Product updated");
    } catch (err) {
      notify.fromError(err, "Failed to save product");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="product-name">Product Name *</Label>
          <Input
            id="product-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="product-sku">SKU</Label>
          <Input id="product-sku" value={product.sku} disabled />
          <p className="text-xs text-muted-foreground">
            SKUs are fixed after creation so receipts and reports stay
            consistent.
          </p>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="product-description">Description</Label>
        <Textarea
          id="product-description"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          rows={2}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="product-category">Category</Label>
          <Select
            value={form.categoryId || "none"}
            onValueChange={(v) =>
              setForm({ ...form, categoryId: v === "none" ? "" : v })
            }
          >
            <SelectTrigger id="product-category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Category</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="product-image">Image URL</Label>
          <Input
            id="product-image"
            value={form.imageUrl}
            onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
            placeholder="https://..."
          />
        </div>
      </div>
      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={updateProduct.isPending}>
          {updateProduct.isPending ? "Saving..." : "Save Details"}
        </Button>
      </div>
    </form>
  );
}
